import React, { useState, useEffect } from 'react'
import {
  Card, Table, Button, Space, Input, Select, DatePicker, Tag, Modal, Typography, Drawer, App
} from 'antd'
import {
  ReloadOutlined, DeleteOutlined, DownloadOutlined, EyeOutlined, SearchOutlined
} from '@ant-design/icons'
import { monitoringApi } from '@/services/api'
import dayjs, { Dayjs } from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select
const { Text, Paragraph } = Typography
const { confirm } = Modal

interface LogRecord {
  id: number
  log_type: string
  endpoint: string
  method: string
  status_code: number
  response_time: number
  error_message: string
  request_data: any
  response_data: any
  ip_address: string
  user_agent: string
  created_at: number
}

const LogManagementPage: React.FC = () => {
  const { message } = App.useApp()
  const [logs, setLogs] = useState<LogRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })

  // 筛选条件
  const [filters, setFilters] = useState({
    logType: '',
    endpoint: '',
    statusCode: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null
  })

  // 日志详情抽屉
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedLog, setSelectedLog] = useState<LogRecord | null>(null)

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true)
      const params = {
        page,
        limit: pagination.pageSize,
        log_type: filters.logType || undefined,
        endpoint: filters.endpoint || undefined,
        status_code: filters.statusCode ? parseInt(filters.statusCode) : undefined,
        startDate: filters.dateRange?.[0]?.valueOf(),
        endDate: filters.dateRange?.[1]?.valueOf(),
        source: 'database'
      }

      const response = await monitoringApi.getApiLogs(params)
      if (response.success) {
        setLogs(response.data)
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.pagination?.total || 0
        }))
      } else {
        message.error('获取日志失败')
      }
    } catch (error) {
      console.error('获取日志失败:', error)
      message.error('获取日志失败')
    } finally {
      setLoading(false)
    }
  }

  const clearLogs = () => {
    confirm({
      title: '确认清空日志',
      content: '此操作将清空所有符合条件的日志记录，是否继续？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const data = {
            target: 'database',
            logType: filters.logType || 'all',
            olderThan: filters.dateRange?.[1]?.valueOf()
          }

          const response = await monitoringApi.clearLogs(data)
          if (response.success) {
            message.success(`已清空 ${response.data?.deleted || 0} 条日志`)
            fetchLogs()
          } else {
            message.error('清空日志失败')
          }
        } catch (error) {
          console.error('清空日志失败:', error)
          message.error('清空日志失败')
        }
      }
    })
  }

  const exportLogs = async () => {
    try {
      const params = {
        logType: filters.logType || 'all',
        format: 'csv',
        startDate: filters.dateRange?.[0]?.valueOf(),
        endDate: filters.dateRange?.[1]?.valueOf(),
        endpoint: filters.endpoint || undefined
      }

      await monitoringApi.exportMonitoringLogs(params)
      message.success('导出成功')
    } catch (error) {
      console.error('导出失败:', error)
      message.error('导出失败')
    }
  }

  const syncFromProxy = async () => {
    try {
      const response = await monitoringApi.syncProxyData()
      if (response.success) {
        message.success('同步成功')
        fetchLogs()
      } else {
        message.error('同步失败')
      }
    } catch (error) {
      console.error('同步失败:', error)
      message.error('同步失败')
    }
  }

  const showLogDetail = (log: LogRecord) => {
    setSelectedLog(log)
    setDetailVisible(true)
  }

  useEffect(() => {
    fetchLogs()
  }, [filters])

  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) return 'success'
    if (statusCode >= 300 && statusCode < 400) return 'warning'
    if (statusCode >= 400 && statusCode < 500) return 'error'
    if (statusCode >= 500) return 'error'
    return 'default'
  }

  const getLogTypeColor = (logType: string) => {
    switch (logType) {
      case 'api': return 'blue'
      case 'error': return 'red'
      case 'auth': return 'green'
      default: return 'default'
    }
  }

  const columns = [
    {
      title: '类型',
      dataIndex: 'log_type',
      key: 'log_type',
      width: 80,
      render: (type: string) => (
        <Tag color={getLogTypeColor(type)}>{type.toUpperCase()}</Tag>
      )
    },
    {
      title: '端点',
      dataIndex: 'endpoint',
      key: 'endpoint',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>{text}</Text>
      )
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (method: string) => (
        <Tag color="blue">{method}</Tag>
      )
    },
    {
      title: '状态码',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 80,
      align: 'center' as const,
      render: (code: number) => (
        <Tag color={getStatusColor(code)}>{code}</Tag>
      )
    },
    {
      title: '响应时间',
      dataIndex: 'response_time',
      key: 'response_time',
      width: 100,
      align: 'right' as const,
      render: (time: number) => time ? `${time}ms` : '-'
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (ip: string) => (
        <Text code>{ip}</Text>
      )
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      ellipsis: true,
      render: (error: string) => error ? (
        <Text type="danger">{error}</Text>
      ) : '-'
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (timestamp: number) => dayjs(timestamp).format('MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: any, record: LogRecord) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showLogDetail(record)}
          size="small"
        >
          详情
        </Button>
      )
    }
  ]

  return (
    <div style={{ padding: '0' }}>
      <Card title="日志管理" extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchLogs()}>
            刷新
          </Button>
          <Button onClick={syncFromProxy}>
            同步Proxy日志
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportLogs}>
            导出
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={clearLogs}>
            清空日志
          </Button>
        </Space>
      }>
        {/* 筛选条件 */}
        <Space wrap style={{ marginBottom: '16px' }}>
          <Select
            placeholder="日志类型"
            value={filters.logType || undefined}
            onChange={value => setFilters(prev => ({ ...prev, logType: value || '' }))}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="api">API</Option>
            <Option value="error">错误</Option>
            <Option value="auth">认证</Option>
          </Select>
          <Input
            placeholder="端点路径"
            value={filters.endpoint}
            onChange={e => setFilters(prev => ({ ...prev, endpoint: e.target.value }))}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="状态码"
            value={filters.statusCode || undefined}
            onChange={value => setFilters(prev => ({ ...prev, statusCode: value || '' }))}
            style={{ width: 120 }}
            allowClear
          >
            <Option value="200">200</Option>
            <Option value="400">400</Option>
            <Option value="401">401</Option>
            <Option value="403">403</Option>
            <Option value="404">404</Option>
            <Option value="500">500</Option>
          </Select>
          <RangePicker
            value={filters.dateRange}
            onChange={value => setFilters(prev => ({ ...prev, dateRange: value as [Dayjs, Dayjs] | null }))}
            placeholder={['开始时间', '结束时间']}
            showTime
          />
        </Space>

        {/* 日志表格 */}
        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={(paginationInfo) => {
            fetchLogs(paginationInfo.current)
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* 日志详情抽屉 */}
      <Drawer
        title="日志详情"
        placement="right"
        width={800}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedLog && (
          <div>
            <Card size="small" title="基本信息" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><strong>类型:</strong> <Tag color={getLogTypeColor(selectedLog.log_type)}>{selectedLog.log_type.toUpperCase()}</Tag></div>
                <div><strong>端点:</strong> <Text code>{selectedLog.endpoint}</Text></div>
                <div><strong>方法:</strong> <Tag color="blue">{selectedLog.method}</Tag></div>
                <div><strong>状态码:</strong> <Tag color={getStatusColor(selectedLog.status_code)}>{selectedLog.status_code}</Tag></div>
                <div><strong>响应时间:</strong> {selectedLog.response_time}ms</div>
                <div><strong>IP地址:</strong> <Text code>{selectedLog.ip_address}</Text></div>
                <div><strong>时间:</strong> {dayjs(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}</div>
                {selectedLog.error_message && (
                  <div><strong>错误信息:</strong> <Text type="danger">{selectedLog.error_message}</Text></div>
                )}
              </Space>
            </Card>

            {selectedLog.user_agent && (
              <Card size="small" title="User-Agent" style={{ marginBottom: 16 }}>
                <Paragraph copyable style={{ margin: 0 }}>
                  {selectedLog.user_agent}
                </Paragraph>
              </Card>
            )}

            {selectedLog.request_data && (
              <Card size="small" title="请求数据" style={{ marginBottom: 16 }}>
                <Paragraph copyable>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                    {JSON.stringify(selectedLog.request_data, null, 2)}
                  </pre>
                </Paragraph>
              </Card>
            )}

            {selectedLog.response_data && (
              <Card size="small" title="响应数据">
                <Paragraph copyable>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                    {JSON.stringify(selectedLog.response_data, null, 2)}
                  </pre>
                </Paragraph>
              </Card>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default LogManagementPage
